import { useEffect, useState } from "react";
import { api, type ClvSummary } from "../lib/api";
import { pct, signClass, signedPct } from "../lib/format";

/**
 * CLV report.
 *
 * Nula je pevná osa histogramu, protože o znaménko tady jde. Kritérium
 * ukončení projektu se opírá právě o to, jestli je průměrné CLV nad nulou.
 */
function Histogram({ values }: { values: number[] }) {
  if (values.length === 0) return null;
  const bins = 21;
  const bound = Math.max(0.05, ...values.map(Math.abs));
  const counts = new Array<number>(bins).fill(0);
  for (const value of values) {
    const index = Math.min(bins - 1, Math.floor(((value + bound) / (2 * bound)) * bins));
    counts[index] = (counts[index] ?? 0) + 1;
  }
  const peak = Math.max(...counts, 1);
  const middle = Math.floor(bins / 2);

  return (
    <>
      <div className="histogram" role="img" aria-label="Rozdělení CLV">
        {counts.map((count, index) => (
          <span
            key={index}
            className={index < middle ? "neg" : index > middle ? "pos" : ""}
            style={{ height: `${(count / peak) * 100}%` }}
          />
        ))}
      </div>
      <div className="axis">
        <span>{signedPct(-bound)}</span>
        <span>0</span>
        <span>{signedPct(bound)}</span>
      </div>
    </>
  );
}

export function Clv() {
  const [data, setData] = useState<{ summary: ClvSummary; values: number[] } | null>(null);
  const [mode, setMode] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.clv(mode || undefined).then(setData).catch((e: Error) => setError(e.message));
  }, [mode]);

  if (error) return <p className="notice warn">{error}</p>;
  if (!data) return <p className="empty">Načítám.</p>;

  const { summary } = data;

  return (
    <>
      <h1>Closing line value</h1>
      <p className="lede">
        CLV je primární kritérium úspěchu, ne ROI. Průměr nad nulou na vzorku několika set signálů
        je jediný důkaz, že systém předbíhá trh. ROI je příliš šumivé, na průkazný závěr by
        potřebovalo tisíce sázek.
      </p>

      <div className="field-row" style={{ marginBottom: "1rem" }}>
        <div className="field" style={{ maxWidth: "12rem" }}>
          <label htmlFor="clv-mode">Režim</label>
          <select id="clv-mode" value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="">vše</option>
            <option value="actionable">jen akční</option>
            <option value="paper">jen papírové</option>
          </select>
        </div>
      </div>

      {summary.n === 0 ? (
        <p className="empty">
          Zatím nemáme žádné vyhodnocené signály. CLV se počítá po skončení zápasu, až se dohledá
          výsledek a zavírací kurz.
        </p>
      ) : (
        <>
          <dl className="readout">
            <div>
              <dt>Signálů</dt>
              <dd className="num">{summary.n}</dd>
            </div>
            <div>
              <dt>Průměrné CLV</dt>
              <dd className={`num ${signClass(summary.meanClvSignal)}`}>
                {signedPct(summary.meanClvSignal, 2)}
              </dd>
            </div>
            <div>
              <dt>Kladných</dt>
              <dd className="num">{pct(summary.positiveShare, 0)}</dd>
            </div>
            {summary.meanClvRealized !== null && (
              <div>
                <dt>CLV z plnění</dt>
                <dd className={`num ${signClass(summary.meanClvRealized)}`}>
                  {signedPct(summary.meanClvRealized, 2)}
                </dd>
              </div>
            )}
            {summary.meanSlippage !== null && (
              <div>
                <dt>Slippage</dt>
                <dd className={`num ${signClass(summary.meanSlippage)}`}>
                  {signedPct(summary.meanSlippage, 2)}
                </dd>
              </div>
            )}
          </dl>

          <Histogram values={data.values} />

          {summary.n < 200 && (
            <p className="notice">
              Vzorek {summary.n} signálů je na závěr malý. Zadání počítá se stovkami, než se dá
              průměrné CLV brát vážně. Interval spolehlivosti spočítá backtest engine příkazem{" "}
              <code>uv run python -m oddsline_quant.cli</code>.
            </p>
          )}
        </>
      )}
    </>
  );
}
